import { ParsedUrlQuery } from 'querystring';
import { API_URL } from 'constants/routes';

import { topics } from '@studenten/shared/constants/filters';
import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react';
import { useMemo } from 'react';
import { CancelTokenSource, fetcher } from 'api_entities/fetch';
import { GeneralSearch } from 'api_entities/generalSearch/types';
import { isKeyPresentInUrl, getKeyValue, getSearchParams } from 'lib/filters';
import { citiesApi } from 'api_entities/cities';
import { generalSearchApi } from 'api_entities/generalSearch';
import { FilterItem } from 'lib/filters/types';

import { IGeneralSearchStore } from './types';

enableStaticRendering(typeof window === 'undefined');

const generateFilterItems = async (
  items: Array<FilterItem | string>,
  filterKey: string,
  totalFacets: Record<string, Record<string, number>>,
  query: ParsedUrlQuery
): Promise<Array<FilterItem>> => {
  if (query[filterKey]) {
    const params = new URLSearchParams();
    params.append('limit', String(10));
    const _query = { ...query };
    delete _query[filterKey];
    Object.entries(_query).map(([key, value]) => {
      if (value instanceof Array) {
        value.forEach((element, idx) => params.append(`${key}[${idx}]`, element));
      } else {
        value && params.append(key, value);
      }
    });
    const { data } = await fetcher.get<GeneralSearch>(API_URL.SEARCH, { params });
    return Promise.resolve(
      items.map((item) => {
        if (typeof item === 'string') {
          return {
            label: item,
            value: item,
            counter: data.totalFacets[filterKey][item] || 0,
            checked: isKeyPresentInUrl(query, filterKey, item),
          };
        } else {
          return {
            label: item.label,
            value: item.value,
            counter: data.totalFacets[filterKey][item.value] || 0,
            checked: isKeyPresentInUrl(query, filterKey, item.value),
          };
        }
      })
    );
  } else {
    return Promise.resolve(
      items.map((item) => {
        if (typeof item === 'string') {
          return {
            label: item,
            value: item,
            counter: totalFacets[filterKey][item] || 0,
            checked: isKeyPresentInUrl(query, filterKey, item),
          };
        } else {
          return {
            label: item.label,
            value: item.value,
            counter: totalFacets[filterKey][item.value] || 0,
            checked: isKeyPresentInUrl(query, filterKey, item.value),
          };
        }
      })
    );
  }
};

let source: CancelTokenSource;
let store: GeneralSearchStore;
export class GeneralSearchStore implements IGeneralSearchStore {
  data: IGeneralSearchStore['data'] = {
    totalCount: 0,
    totalFacets: {},
  };
  filters: IGeneralSearchStore['filters'] = {
    city: [],
    province: [],
    categories: topics,
  };
  constructor() {
    makeAutoObservable(this);
  }
  async deleteAllSelectedFilters(query: ParsedUrlQuery) {
    await this.fetchData(query);
    await this.fetchFilters(query, null);
  }
  async deleteSelectedFilter(
    query: ParsedUrlQuery,
    key: keyof IGeneralSearchStore['filters'],
    value: string
  ) {
    const targetItem: FilterItem | undefined = getKeyValue(this.filters)(key).find(
      (item: FilterItem) => item.value === value
    );
    if (targetItem?.checked) {
      targetItem.checked = false;
    }

    await this.fetchFilters(query, key);
  }
  toggleFilters = (data: Array<FilterItem>, filterKey: keyof IGeneralSearchStore['filters']) => {
    this.filters = {
      ...this.filters,
      [filterKey]: (this.filters[filterKey] as Array<FilterItem>).map((item, idx) => ({
        ...item,
        checked: data[idx].checked,
      })),
    };
  };
  async fetchFilters(
    query: ParsedUrlQuery,
    filterKey: keyof IGeneralSearchStore['filters'] | null
  ) {
    try {
      if (filterKey) {
        await this.fetchData(query);
        Object.entries(this.filters).map(async ([key]) => {
          if (query[key] && key !== 'categories') {
            const params = new URLSearchParams();
            params.append('limit', String(10));
            const _query = { ...query };
            delete _query[key];
            Object.entries(_query).map(([key, value]) => {
              if (value instanceof Array) {
                value.forEach((element, idx) => params.append(`${key}[${idx}]`, element));
              } else {
                value && params.append(key, value);
              }
            });
            const { data } = await fetcher.get<GeneralSearch>(API_URL.SEARCH, { params });
            this.filters[key] = this.filters[key].map((subItem) => {
              return {
                ...subItem,
                counter: data.totalFacets?.[key]?.[subItem.value] || 0,
              };
            });
          } else if (key === 'categories') {
            const params = new URLSearchParams();
            params.append('limit', String(10));
            const _query = { ...query };
            delete _query[key];
            Object.entries(_query).map(([key, value]) => {
              if (value instanceof Array) {
                value.forEach((element, idx) => params.append(`${key}[${idx}]`, element));
              } else {
                value && params.append(key, value);
              }
            });
            const { data } = await fetcher.get<GeneralSearch>(API_URL.SEARCH, { params });
            this.filters.categories = this.filters.categories.map((categoryItem) => {
              const target = categoryItem.value as Exclude<
                keyof IGeneralSearchStore['data'],
                'totalFacets' | 'totalCount'
              >;
              return {
                ...categoryItem,
                counter: data?.[target]?.totalCount || 0,
              };
            });
          } else {
            this.filters[key] = this.filters[key].map((subItem) => {
              return {
                ...subItem,
                counter: this.data.totalFacets?.[key]?.[subItem.value] || 0,
              };
            });
          }
        });
      } else {
        const [citiesRes, provincesRes] = await citiesApi.getCitiesAndProvinces();

        this.filters = {
          city: await generateFilterItems(
            citiesRes.data.data,
            'city',
            this.data.totalFacets,
            query
          ),
          province: await generateFilterItems(
            provincesRes.data.data,
            'province',
            this.data.totalFacets,
            query
          ),
          categories: this.filters.categories.map(({ value, label }) => ({
            label,
            value,
            counter:
              this.data[
                value as Exclude<keyof IGeneralSearchStore['data'], 'totalFacets' | 'totalCount'>
              ]?.totalCount || 0,
            checked: query['categories']
              ? query['categories'] instanceof Array
                ? query['categories'].includes(label)
                : query['categories'] === label
              : false,
          })),
        };
      }
    } catch (error) {
      console.error(error);
    }
    return null;
  }
  hydrate(
    initialData: IGeneralSearchStore['data'],
    initialFilters: IGeneralSearchStore['filters']
  ) {
    this.data = initialData;
    this.filters = initialFilters;
  }
  async fetchData(query: ParsedUrlQuery) {
    const params = getSearchParams(query, false, 5);
    if (source) {
      source.cancel('Operation canceled by the user.');
    }
    source = fetcher.CancelToken.source();
    const data = await generalSearchApi.getGeneralSearch(params, source);
    runInAction(() => {
      this.data = data;
    });
  }
}

function initializeStore(
  initialData: IGeneralSearchStore['data'],
  initialFilters: IGeneralSearchStore['filters']
) {
  const _store = store ?? new GeneralSearchStore();

  // If your page has Next.js data fetching methods that use a Mobx store, it will
  // get hydrated here

  if (initialData) {
    _store.hydrate(initialData, initialFilters);
  }
  // For SSG and SSR always create a new store
  if (typeof window === 'undefined') return _store;
  // Create the store once in the client
  if (!store) store = _store;

  return _store;
}

export function useStore(
  initialData: IGeneralSearchStore['data'],
  initialFilters: IGeneralSearchStore['filters']
) {
  const store = useMemo(() => initializeStore(initialData, initialFilters), [
    initialData,
    initialFilters,
  ]);
  return store;
}
